/**
 * Compact preview mock of how a Piece will render natively on its platform.
 * Not pixel-perfect; just enough fidelity so the editor + preview side-by-side
 * help the user reason about copy + media + structure.
 */

import { useTranslation } from "react-i18next";
import { type PostType, POST_TYPE_META } from "@/hooks/use-post-generation";
import { Heart, MessageCircle, Send, Bookmark, Repeat2, ThumbsUp, Play, FileText, ArrowUpRight, Globe } from "lucide-react";

export interface PostTypePreviewDraft {
  caption: string;
  title: string | null;
  description: string | null;
  hashtags: string[];
  threadItems: Array<{ content: string; mediaUrls?: string[] }> | null;
  platformKnobs: Record<string, any>;
  mediaUrls: string[];
}

interface Props {
  postType: PostType;
  draft: PostTypePreviewDraft;
  brandHandle?: string | null;
  brandName?: string | null;
}

function captionWithHashtags(caption: string, hashtags: string[]): string {
  if (!hashtags.length) return caption;
  return `${caption}\n\n${hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}`;
}

function MediaThumb({ url, aspect = "square" }: { url: string; aspect?: "square" | "portrait" | "landscape" | "vertical" }) {
  const { t } = useTranslation("compose");
  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
  const isPdf = /\.pdf(\?|$)/i.test(url);
  const aspectClass = {
    square: "aspect-square",
    portrait: "aspect-[4/5]",
    landscape: "aspect-[1.91/1]",
    vertical: "aspect-[9/16]",
  }[aspect];
  if (isPdf) {
    return (
      <div className={`${aspectClass} bg-muted flex flex-col items-center justify-center gap-1 text-muted-foreground`}>
        <FileText className="h-8 w-8" />
        <span className="text-xs">{t("preview.pdfDocument")}</span>
      </div>
    );
  }
  if (isVideo) {
    return (
      <div className={`${aspectClass} bg-black flex items-center justify-center text-white relative`}>
        <Play className="h-8 w-8 opacity-80" fill="currentColor" />
      </div>
    );
  }
  return <img src={url} alt="" className={`${aspectClass} object-cover w-full bg-muted`} />;
}

function HandleRow({ handle, suffix }: { handle: string; suffix?: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-semibold">
        {handle.charAt(0).toUpperCase()}
      </div>
      <span className="font-medium">{handle}</span>
      {suffix && <span className="text-muted-foreground">{suffix}</span>}
    </div>
  );
}

export function PostTypePreview({ postType, draft, brandHandle, brandName }: Props) {
  const { t } = useTranslation("compose");
  const handle = brandHandle ?? brandName ?? "brand";
  const meta = POST_TYPE_META[postType];

  // ─── Instagram ────────────────────────────────────────────
  if (postType === "IG_FEED" || postType === "IG_CAROUSEL") {
    return (
      <PreviewCard label={`${meta.platform} · ${meta.label}`} platform="INSTAGRAM">
        <HandleRow handle={handle} />
        <div className="-mx-3 mt-2 relative">
          {draft.mediaUrls.length > 0 ? (
            <MediaThumb url={draft.mediaUrls[0]} aspect="square" />
          ) : (
            <div className="aspect-square bg-muted flex items-center justify-center text-xs text-muted-foreground">
              {t("preview.imageWillAppear")}
            </div>
          )}
          {postType === "IG_CAROUSEL" && draft.mediaUrls.length > 1 && (
            <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full text-[10px] px-2 py-0.5">
              1/{draft.mediaUrls.length}
            </span>
          )}
        </div>
        <ActionsRow icons={[Heart, MessageCircle, Send, Bookmark]} />
        <Caption username={handle} text={captionWithHashtags(draft.caption, draft.hashtags)} />
      </PreviewCard>
    );
  }

  if (postType === "IG_REEL") {
    return (
      <PreviewCard label="Instagram · Reel" platform="INSTAGRAM">
        <div className="-mx-3 -mt-3 bg-black">
          {draft.mediaUrls[0] ? (
            <MediaThumb url={draft.mediaUrls[0]} aspect="vertical" />
          ) : (
            <div className="aspect-[9/16] bg-muted flex items-center justify-center text-xs text-muted-foreground">
              {t("preview.videoWillAppear")}
            </div>
          )}
        </div>
        <Caption username={handle} text={captionWithHashtags(draft.caption, draft.hashtags)} />
      </PreviewCard>
    );
  }

  if (postType === "IG_STORY" || postType === "FB_STORY") {
    return (
      <PreviewCard label={`${meta.platform} · Story`} platform={meta.platform}>
        <div className="-mx-3 -mt-3 bg-black relative">
          {draft.mediaUrls[0] ? (
            <MediaThumb url={draft.mediaUrls[0]} aspect="vertical" />
          ) : (
            <div className="aspect-[9/16] bg-muted" />
          )}
          {draft.caption && (
            <div className="absolute inset-x-3 bottom-3 text-white text-xs bg-black/40 rounded px-2 py-1">
              {draft.caption}
            </div>
          )}
        </div>
      </PreviewCard>
    );
  }

  // ─── Facebook ─────────────────────────────────────────────
  if (postType === "FB_FEED") {
    return (
      <PreviewCard label="Facebook · Feed" platform="FACEBOOK">
        <HandleRow handle={handle} suffix="· Just now" />
        <Body text={captionWithHashtags(draft.caption, draft.hashtags)} />
        {draft.mediaUrls[0] && (
          <div className="-mx-3 mt-2">
            <MediaThumb url={draft.mediaUrls[0]} aspect="landscape" />
          </div>
        )}
        <ActionsRow icons={[ThumbsUp, MessageCircle, Send]} />
      </PreviewCard>
    );
  }
  if (postType === "FB_REEL") {
    return (
      <PreviewCard label="Facebook · Reel" platform="FACEBOOK">
        <div className="-mx-3 -mt-3 bg-black">
          {draft.mediaUrls[0] ? <MediaThumb url={draft.mediaUrls[0]} aspect="vertical" /> : <div className="aspect-[9/16] bg-muted" />}
        </div>
        {draft.title && <div className="text-sm font-medium mt-2">{draft.title}</div>}
        <Caption username={handle} text={captionWithHashtags(draft.caption, draft.hashtags)} />
      </PreviewCard>
    );
  }

  // ─── LinkedIn ─────────────────────────────────────────────
  if (postType === "LINKEDIN_TEXT_OR_IMAGE" || postType === "LINKEDIN_DOC") {
    return (
      <PreviewCard label={`LinkedIn · ${meta.label}`} platform="LINKEDIN">
        <HandleRow handle={handle} suffix="· Just now" />
        <Body text={captionWithHashtags(draft.caption, draft.hashtags)} />
        {draft.mediaUrls[0] && (
          <div className="-mx-3 mt-2">
            <MediaThumb
              url={draft.mediaUrls[0]}
              aspect={postType === "LINKEDIN_DOC" ? "square" : "landscape"}
            />
          </div>
        )}
        {postType === "LINKEDIN_DOC" && draft.title && (
          <div className="text-xs text-muted-foreground mt-1">Document: {draft.title}</div>
        )}
        <ActionsRow icons={[ThumbsUp, MessageCircle, Repeat2, Send]} />
      </PreviewCard>
    );
  }

  // ─── X / Threads ──────────────────────────────────────────
  if (postType === "X_TWEET" || postType === "THREADS_POST") {
    return (
      <PreviewCard label={`${meta.platform === "TWITTER" ? "X" : "Threads"} · ${meta.label}`} platform={meta.platform}>
        <HandleRow handle={`@${handle}`} suffix="· now" />
        <Body text={draft.caption} />
        {draft.mediaUrls.length > 0 && (
          <div className="-mx-3 mt-2 grid grid-cols-2 gap-0.5 rounded overflow-hidden">
            {draft.mediaUrls.slice(0, 4).map((u, i) => (
              <MediaThumb key={i} url={u} aspect="landscape" />
            ))}
          </div>
        )}
        <ActionsRow icons={[MessageCircle, Repeat2, Heart, Send]} />
      </PreviewCard>
    );
  }
  if (postType === "X_THREAD" || postType === "THREADS_THREAD") {
    const items = draft.threadItems ?? [];
    return (
      <PreviewCard label={`${meta.platform === "TWITTER" ? "X" : "Threads"} · Thread`} platform={meta.platform}>
        {items.length === 0 ? (
          <div className="text-xs text-muted-foreground">{t("preview.noThreadItems")}</div>
        ) : (
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i} className="border-l-2 border-muted pl-3">
                <HandleRow handle={`@${handle}`} suffix={i === 0 ? "· now" : `· #${i + 1}`} />
                <Body text={it.content} />
                {it.mediaUrls && it.mediaUrls.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-0.5 rounded overflow-hidden">
                    {it.mediaUrls.slice(0, 4).map((u, j) => (
                      <MediaThumb key={j} url={u} aspect="landscape" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </PreviewCard>
    );
  }

  // ─── TikTok ───────────────────────────────────────────────
  if (postType === "TIKTOK_VIDEO" || postType === "TIKTOK_PHOTO") {
    return (
      <PreviewCard label={`TikTok · ${meta.label}`} platform="TIKTOK">
        <div className="-mx-3 -mt-3 bg-black">
          {draft.mediaUrls[0] ? (
            <MediaThumb url={draft.mediaUrls[0]} aspect="vertical" />
          ) : (
            <div className="aspect-[9/16] bg-muted" />
          )}
        </div>
        <HandleRow handle={`@${handle}`} />
        <Body text={captionWithHashtags(draft.caption, draft.hashtags)} small />
      </PreviewCard>
    );
  }

  // ─── YouTube ──────────────────────────────────────────────
  if (postType === "YOUTUBE_SHORT") {
    return (
      <PreviewCard label="YouTube · Short" platform="YOUTUBE">
        <div className="-mx-3 -mt-3 bg-black">
          {draft.mediaUrls[0] ? (
            <MediaThumb url={draft.mediaUrls[0]} aspect="vertical" />
          ) : (
            <div className="aspect-[9/16] bg-muted" />
          )}
        </div>
        <div className="text-sm font-medium mt-2">{draft.title || t("preview.untitledShort")}</div>
        <Body text={captionWithHashtags(draft.caption, draft.hashtags)} small />
      </PreviewCard>
    );
  }
  if (postType === "YOUTUBE_LONG") {
    return (
      <PreviewCard label="YouTube · Long-form" platform="YOUTUBE">
        <div className="-mx-3 -mt-3">
          {draft.mediaUrls[0] ? (
            <MediaThumb url={draft.mediaUrls[0]} aspect="landscape" />
          ) : (
            <div className="aspect-video bg-muted" />
          )}
        </div>
        <div className="text-sm font-medium mt-2">{draft.title || t("preview.untitledVideo")}</div>
        <Body text={draft.description ?? ""} small />
        <ActionsRow icons={[ThumbsUp, MessageCircle, Send]} />
      </PreviewCard>
    );
  }

  // ─── Reddit ───────────────────────────────────────────────
  if (postType === "REDDIT_LINK" || postType === "REDDIT_MEDIA" || postType === "REDDIT_SELF") {
    const subreddit = draft.platformKnobs.subreddit as string | undefined;
    const url = draft.platformKnobs.url as string | undefined;
    return (
      <PreviewCard label={`Reddit · ${meta.label}`} platform="REDDIT">
        <div className="text-xs text-muted-foreground">
          r/{subreddit ?? "<subreddit>"} · posted by u/{handle}
        </div>
        <div className="text-sm font-medium mt-1">{draft.title || t("preview.noTitle")}</div>
        {postType === "REDDIT_LINK" && url && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <ArrowUpRight className="h-3 w-3" />
            <span className="truncate">{url}</span>
          </div>
        )}
        {postType === "REDDIT_SELF" && draft.caption && (
          <div className="text-xs whitespace-pre-wrap mt-2">{draft.caption}</div>
        )}
        {postType === "REDDIT_MEDIA" && draft.mediaUrls[0] && (
          <div className="-mx-3 mt-2">
            <MediaThumb url={draft.mediaUrls[0]} aspect="landscape" />
          </div>
        )}
      </PreviewCard>
    );
  }

  // ─── GBP ──────────────────────────────────────────────────
  if (postType === "GBP_UPDATE") {
    const cta = draft.platformKnobs.callToAction as
      | { type?: string; url?: string }
      | undefined;
    return (
      <PreviewCard label="Google Business · Update" platform="GOOGLEBUSINESS">
        <div className="flex items-center gap-2 text-xs">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{brandName ?? handle}</span>
        </div>
        {draft.mediaUrls[0] && (
          <div className="-mx-3 mt-2">
            <MediaThumb url={draft.mediaUrls[0]} aspect="landscape" />
          </div>
        )}
        <Body text={draft.caption} />
        {cta?.type && (
          <button
            type="button"
            className="text-xs font-medium text-blue-700 hover:underline mt-1"
            onClick={(e) => e.preventDefault()}
          >
            {cta.type.replace(/_/g, " ")} →
          </button>
        )}
      </PreviewCard>
    );
  }

  // Fallback
  return (
    <PreviewCard label={`${meta.platform} · ${meta.label}`} platform={meta.platform}>
      <Body text={captionWithHashtags(draft.caption, draft.hashtags)} />
      {draft.mediaUrls[0] && (
        <div className="-mx-3 mt-2">
          <MediaThumb url={draft.mediaUrls[0]} aspect="landscape" />
        </div>
      )}
    </PreviewCard>
  );
}

function PreviewCard({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
  platform: string;
}) {
  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40 px-3 py-1 border-b">
        {label}
      </div>
      <div className="p-3 space-y-1">{children}</div>
    </div>
  );
}

function ActionsRow({ icons }: { icons: Array<React.ComponentType<{ className?: string }>> }) {
  return (
    <div className="flex items-center gap-3 mt-2 text-muted-foreground">
      {icons.map((Icon, i) => (
        <Icon key={i} className="h-4 w-4" />
      ))}
    </div>
  );
}

function Body({ text, small }: { text: string; small?: boolean }) {
  if (!text) return null;
  return (
    <div className={`whitespace-pre-wrap ${small ? "text-xs" : "text-sm"}`}>{text}</div>
  );
}

function Caption({ username, text }: { username: string; text: string }) {
  if (!text) return null;
  return (
    <div className="text-xs mt-1">
      <span className="font-semibold">{username}</span>{" "}
      <span className="whitespace-pre-wrap">{text}</span>
    </div>
  );
}
